import React, { useState, useEffect, useContext, useRef } from 'react'
import Context from '../utils/Context'
import ROSLIB from 'roslib'

const Cameras = () => {
  const context = useContext(Context)
  
  const [cameraFeeds, setCameraFeeds] = useState({
    rear: { active: false, lastUpdate: null },
    front: { active: false, lastUpdate: null },
    left: { active: false, lastUpdate: null },
    right: { active: false, lastUpdate: null }
  })
  
  const [cameraEnabled, setCameraEnabled] = useState({
    rear: true,
    front: true,
    left: true,
    right: true
  })
  
  const [cameraLoading, setCameraLoading] = useState({
    rear: false,
    front: false,
    left: false,
    right: false
  })
  
  const canvasRefs = useRef({
    rear: null,
    front: null,
    left: null,
    right: null
  })
  
  const canvasInitialized = useRef({
    rear: false,
    front: false,
    left: false,
    right: false
  })
  
  const subscribers = useRef({
    rear: null,
    front: null,
    left: null,
    right: null
  })
  
  const [isConnected, setIsConnected] = useState(false)
  
  // Camera topics configuration
  const cameraTopics = {
    rear: '/rear/color/image_raw',
    front: '/front/color/image_raw',
    left: '/left/color/image_raw',
    right: '/right/color/image_raw'
  }
  
  useEffect(() => {
    if (context.selectedRobot && window.ros && window.rosConnected) {
      setIsConnected(true)
      subscribeToCameras()
    } else {
      setIsConnected(false)
      unsubscribeAll()
    }
    
    return () => {
      unsubscribeAll()
    }
  }, [context.selectedRobot, window.rosConnected])
  
  const subscribeToCameras = () => {
    Object.keys(cameraTopics).forEach(camera => {
      if (cameraEnabled[camera]) {
        subscribeToCamera(camera, cameraTopics[camera])
      }
    })
  }
  
  const toggleCamera = (cameraName) => {
    const newState = !cameraEnabled[cameraName]
    
    setCameraEnabled(prev => ({
      ...prev,
      [cameraName]: newState
    }))
    
    if (newState) {
      // Liga a câmera
      console.log(`🟢 Ligando câmera: ${cameraName}`)
      setCameraLoading(prev => ({ ...prev, [cameraName]: true }))
      subscribeToCamera(cameraName, cameraTopics[cameraName])
    } else {
      // Desliga a câmera
      console.log(`🔴 Desligando câmera: ${cameraName}`)
      unsubscribeCamera(cameraName)
      canvasInitialized.current[cameraName] = false
      setCameraLoading(prev => ({ ...prev, [cameraName]: false }))
      setCameraFeeds(prev => ({
        ...prev,
        [cameraName]: { active: false, lastUpdate: null }
      }))
    }
  }
  
  const unsubscribeCamera = (cameraName) => {
    const subs = subscribers.current[cameraName]
    if (subs) {
      if (subs.compressed) subs.compressed.unsubscribe()
      if (subs.raw) subs.raw.unsubscribe()
      subscribers.current[cameraName] = null
    }
  }
  
  const subscribeToCamera = (cameraName, topicName) => {
    if (subscribers.current[cameraName]) {
      const subs = subscribers.current[cameraName]
      if (subs.compressed) subs.compressed.unsubscribe()
      if (subs.raw) subs.raw.unsubscribe()
    }
    
    console.log(`📹 Subscrevendo em: ${topicName}`)
    
    // Primeiro tenta com CompressedImage (mais comum para web)
    const compressedTopic = new ROSLIB.Topic({
      ros: window.ros,
      name: topicName + '/compressed',
      messageType: 'sensor_msgs/CompressedImage',
      throttle_rate: 15,   // ~30 FPS para melhor fluidez
      queue_length: 1      // Mantém apenas a mensagem mais recente
    })
    
    compressedTopic.subscribe((message) => {
      console.log(`✅ [${cameraName}] Imagem COMPRIMIDA recebida:`, {
        format: message.format,
        dataLength: message.data?.length
      })
      handleCompressedImage(cameraName, message)
    })
    
    // Também subscreve no raw como fallback
    const rawTopic = new ROSLIB.Topic({
      ros: window.ros,
      name: topicName,
      messageType: 'sensor_msgs/Image',
      throttle_rate: 15,   // ~30 FPS para melhor fluidez
      queue_length: 1      // Mantém apenas a mensagem mais recente
    })
    
    rawTopic.subscribe((message) => {
      console.log(`✅ [${cameraName}] Imagem RAW recebida:`, {
        width: message.width,
        height: message.height,
        encoding: message.encoding,
        step: message.step,
        dataLength: message.data?.length,
        dataType: typeof message.data,
        isArray: Array.isArray(message.data)
      })
      handleCameraData(cameraName, message)
    })
    
    subscribers.current[cameraName] = { compressed: compressedTopic, raw: rawTopic }
    
    // Check for timeout (no signal)
    setTimeout(() => {
      checkCameraTimeout(cameraName)
    }, 5000)
  }
  
  const handleCompressedImage = (cameraName, message) => {
    setCameraFeeds(prev => ({
      ...prev,
      [cameraName]: {
        active: true,
        lastUpdate: Date.now()
      }
    }))
    
    // Remove loading quando receber primeira imagem
    setCameraLoading(prev => ({ ...prev, [cameraName]: false }))
    
    const canvas = canvasRefs.current[cameraName]
    if (!canvas) return
    
    const ctx = canvas.getContext('2d', { 
      alpha: false,           // Sem transparência para melhor performance
      willReadFrequently: false // Otimização para escrita
    })
    
    // Converte base64 para blob e desenha
    const img = new Image()
    img.onload = () => {
      // Inicializa canvas apenas uma vez para evitar piscar
      if (!canvasInitialized.current[cameraName]) {
        canvas.width = img.width
        canvas.height = img.height
        canvasInitialized.current[cameraName] = true
        console.log(`🎬 [${cameraName}] Canvas inicializado: ${img.width}x${img.height}`)
      }
      
      // Desenha diretamente sem clear - a nova imagem sobrescreve a anterior
      ctx.drawImage(img, 0, 0)
    }
    img.onerror = (err) => {
      console.error(`❌ [${cameraName}] Erro ao carregar imagem:`, err)
    }
    
    // message.data vem como base64 ou array de bytes
    if (typeof message.data === 'string') {
      img.src = 'data:image/jpeg;base64,' + message.data
    } else {
      // Converte array de bytes para base64
      const base64 = btoa(String.fromCharCode.apply(null, new Uint8Array(message.data)))
      img.src = 'data:image/jpeg;base64,' + base64
    }
  }
  
  const handleCameraData = (cameraName, message) => {
    setCameraFeeds(prev => ({
      ...prev,
      [cameraName]: {
        active: true,
        lastUpdate: Date.now()
      }
    }))
    
    // Remove loading quando receber primeira imagem
    setCameraLoading(prev => ({ ...prev, [cameraName]: false }))
    
    // Draw image on canvas
    drawImageOnCanvas(cameraName, message)
  }
  
  const drawImageOnCanvas = (cameraName, message) => {
    const canvas = canvasRefs.current[cameraName]
    if (!canvas) {
      console.warn(`⚠️ [${cameraName}] Canvas não encontrado`)
      return
    }
    
    const ctx = canvas.getContext('2d', { 
      alpha: false,           // Sem transparência
      willReadFrequently: false // Otimização para escrita
    })
    const { width, height, data, encoding, step } = message
    
    console.log(`🎨 [${cameraName}] Desenhando ${encoding} ${width}x${height}`)
    
    if (!data || data.length === 0) {
      console.error(`❌ [${cameraName}] Sem dados de imagem`)
      return
    }
    
    // Inicializa canvas apenas uma vez
    if (!canvasInitialized.current[cameraName]) {
      canvas.width = width
      canvas.height = height
      canvasInitialized.current[cameraName] = true
      console.log(`🎬 [${cameraName}] Canvas inicializado: ${width}x${height}`)
    }
    
    // Converte data para Uint8Array se necessário
    const imageBytes = data instanceof Uint8Array ? data : new Uint8Array(data)
    
    // Create ImageData from ROS message
    const imageData = ctx.createImageData(width, height)
    
    // Convert based on encoding type
    if (encoding === 'rgb8') {
      for (let i = 0; i < width * height; i++) {
        const srcIdx = i * 3
        const dstIdx = i * 4
        imageData.data[dstIdx] = imageBytes[srcIdx]       // R
        imageData.data[dstIdx + 1] = imageBytes[srcIdx + 1] // G
        imageData.data[dstIdx + 2] = imageBytes[srcIdx + 2] // B
        imageData.data[dstIdx + 3] = 255                    // A
      }
    } else if (encoding === 'bgr8') {
      for (let i = 0; i < width * height; i++) {
        const srcIdx = i * 3
        const dstIdx = i * 4
        imageData.data[dstIdx] = imageBytes[srcIdx + 2]     // R
        imageData.data[dstIdx + 1] = imageBytes[srcIdx + 1] // G
        imageData.data[dstIdx + 2] = imageBytes[srcIdx]     // B
        imageData.data[dstIdx + 3] = 255                    // A
      }
    } else if (encoding === 'rgba8') {
      imageData.data.set(imageBytes)
    } else if (encoding === 'bgra8') {
      for (let i = 0; i < width * height; i++) {
        const srcIdx = i * 4
        const dstIdx = i * 4
        imageData.data[dstIdx] = imageBytes[srcIdx + 2]     // R
        imageData.data[dstIdx + 1] = imageBytes[srcIdx + 1] // G
        imageData.data[dstIdx + 2] = imageBytes[srcIdx]     // B
        imageData.data[dstIdx + 3] = imageBytes[srcIdx + 3] // A
      }
    } else if (encoding === 'mono8' || encoding === '8UC1') {
      for (let i = 0; i < width * height; i++) {
        const dstIdx = i * 4
        const value = imageBytes[i]
        imageData.data[dstIdx] = value
        imageData.data[dstIdx + 1] = value
        imageData.data[dstIdx + 2] = value
        imageData.data[dstIdx + 3] = 255
      }
    } else {
      console.warn(`⚠️ [${cameraName}] Encoding não suportado: ${encoding}`)
      return
    }
    
    ctx.putImageData(imageData, 0, 0)
    console.log(`✅ [${cameraName}] Imagem renderizada com sucesso`)
  }
  
  const checkCameraTimeout = (cameraName) => {
    const feed = cameraFeeds[cameraName]
    const now = Date.now()
    
    if (!feed.lastUpdate || (now - feed.lastUpdate) > 3000) {
      // Ativa loading para mostrar que está reconectando
      if (cameraEnabled[cameraName]) {
        setCameraLoading(prev => ({ ...prev, [cameraName]: true }))
      }
      
      setCameraFeeds(prev => ({
        ...prev,
        [cameraName]: {
          ...prev[cameraName],
          active: false
        }
      }))
    }
  }
  
  const unsubscribeAll = () => {
    Object.keys(subscribers.current).forEach(camera => {
      const subs = subscribers.current[camera]
      if (subs) {
        if (subs.compressed) subs.compressed.unsubscribe()
        if (subs.raw) subs.raw.unsubscribe()
        subscribers.current[camera] = null
      }
    })
  }
  
  // Periodically check for camera timeouts
  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(cameraFeeds).forEach(camera => {
        checkCameraTimeout(camera)
      })
    }, 2000)
    
    return () => clearInterval(interval)
  }, [cameraFeeds])
  
  const renderCamera = (cameraName, title) => {
    const feed = cameraFeeds[cameraName]
    const isEnabled = cameraEnabled[cameraName]
    const isLoading = cameraLoading[cameraName]
    const hasImage = canvasInitialized.current[cameraName]
    
    return (
      <div className="column is-6" key={cameraName}>
        <div className="box" style={{ minHeight: '300px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 className="title is-5 mb-0">{title}</h3>
            <button 
              className={`button is-small ${isEnabled ? 'is-danger' : 'is-success'}`}
              onClick={() => toggleCamera(cameraName)}
              title={isEnabled ? 'Desligar câmera' : 'Ligar câmera'}
            >
              {isEnabled ? '🔴 OFF' : '🟢 ON'}
            </button>
          </div>
          <div style={{ position: 'relative', width: '100%', height: '250px', backgroundColor: '#000' }}>
            {/* Overlay quando câmera está desligada */}
            {!isEnabled && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                flexDirection: 'column'
              }}>
                <div style={{ color: '#888', fontSize: '1.5rem', fontWeight: 'bold' }}>OFF</div>
                <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '10px' }}>Câmera desligada</div>
              </div>
            )}
            
            {/* Overlay de loading/reconnecting */}
            {isEnabled && isLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: hasImage ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
                flexDirection: 'column'
              }}>
                <div className="loader" style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #3498db',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div style={{ color: '#fff', marginTop: '15px', fontSize: '0.9rem' }}>
                  {hasImage ? 'Reconectando...' : 'Carregando...'}
                </div>
              </div>
            )}
            
            {/* Canvas - sempre visível quando habilitado para manter imagem congelada */}
            <canvas
              ref={el => canvasRefs.current[cameraName] = el}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: isEnabled ? 'block' : 'none'
              }}
            />
          </div>
          <div className="has-text-centered mt-2">
            {isEnabled ? (
              <span className={`tag ${feed.active ? 'is-success' : (isLoading ? 'is-warning' : 'is-danger')}`}>
                {feed.active ? 'Conectado' : (isLoading ? 'Reconectando' : 'Desconectado')}
              </span>
            ) : (
              <span className="tag is-dark">Desligada</span>
            )}
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="container">
        <div className="notification is-info is-light mb-4">
        <p className="has-text-centered">
          <strong>Status da Conexão ROS:</strong> {isConnected ? '✓ Conectado' : '✗ Desconectado'}
        </p>
      </div>
      
      <div className="columns is-multiline">
        {renderCamera('front', 'Câmera Frontal')}
        {renderCamera('rear', 'Câmera Traseira')}
        {renderCamera('left', 'Câmera Esquerda')}
        {renderCamera('right', 'Câmera Direita')}
      </div>
      
      <div className="notification is-warning is-light mt-4">
        <p className="is-size-7">
          <strong>Tópicos ROS monitorados:</strong>
        </p>
        <ul className="is-size-7 ml-4">
          {Object.entries(cameraTopics).map(([camera, topic]) => (
            <li key={camera}>
              <code>{topic}</code> - {cameraFeeds[camera].active ? '✓' : '✗'}
            </li>
          ))}
        </ul>
      </div>
      </div>
    </>
  )
}

export default Cameras
